import { useCallback, useRef } from 'react';

import { useTheme } from '@mui/material';
import { sanitizeHtml } from '@realadvisor/html-sanitizer';
import { fixGluedEmphasis } from '@realadvisor/html-sanitizer/fix-glued-emphasis';
import { marked } from 'marked';
import Quill from 'quill';
import TurndownService from 'turndown';

import { ComposerToolbar } from '@modules/_shared/components/ComposerToolbar';
import { matchWordListParagraph } from '@modules/_shared/components/quillClipboardMatchers';
import {
  qlEditorBaseStyles,
  qlEditorSurfaceStyles,
  qlListStyles,
} from '@modules/_shared/components/quillStyles';
import { RichEditorOutline } from '@modules/_shared/components/RichEditorOutline';
import {
  type QuillClipboardMatcher,
  useQuillEditor,
} from '@modules/_shared/components/useQuillEditor';

const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  emDelimiter: '_',
  strongDelimiter: '**',
  linkStyle: 'inlined',
});
turndownService.addRule('lineBreak', { filter: 'br', replacement: () => '\n' });
turndownService.addRule('listItemSingleSpace', {
  filter: 'li',
  replacement: (content, node, options) => {
    const body = content
      .replace(/^\n+/, '')
      .replace(/\n+$/, '\n')
      .replace(/\n/gm, '\n  ');
    let prefix = options.bulletListMarker + ' ';
    const parent = node.parentNode as HTMLElement;
    if (parent.nodeName === 'OL') {
      const start = parent.getAttribute('start');
      const index = Array.prototype.indexOf.call(parent.children, node);
      prefix = (start ? Number(start) + index : index + 1) + '. ';
    }
    return prefix + body + (node.nextSibling && !/\n$/.test(body) ? '\n' : '');
  },
});

export const convertHtmlToMarkdown = (html: string): string =>
  turndownService
    .turndown(html)
    .replace(/\u00A0/g, ' ')
    .trim();

const markdownToHtml = (
  markdown: string,
  preserveLineBreaks: boolean,
): string =>
  marked.parse(fixGluedEmphasis(markdown), {
    async: false,
    breaks: preserveLineBreaks,
  }) as string;

const startsAsHtml = (s: string) =>
  /^\s*<(?:blockquote|div|h[1-6]|ol|p|pre|table|ul)(?:\s|>)/i.test(s);

const matchTable = (node: HTMLElement, _delta: unknown): any => {
  const Delta = Quill.import('delta') as any;
  const rows = Array.from(node.querySelectorAll('tr'))
    .map(row =>
      Array.from(row.querySelectorAll('th, td'))
        .map(cell => cell.textContent?.trim() ?? '')
        .join(' | '),
    )
    .filter(Boolean);
  return new Delta().insert(`${rows.join('\n')}\n`);
};

const ignoreExternalElement = (): any => {
  const Delta = Quill.import('delta') as any;
  return new Delta();
};

const MARKDOWN_FORMATS = ['bold', 'italic', 'indent', 'list', 'link'];

const MARKDOWN_MATCHERS: ReadonlyArray<
  readonly [string, QuillClipboardMatcher]
> = [
  ['script', ignoreExternalElement],
  ['style', ignoreExternalElement],
  ['table', matchTable],
  ['p[class*="MsoListParagraph"]', matchWordListParagraph],
];

export const MarkdownComposer = ({
  readOnly = false,
  placeholder,
  minHeight,
  maxHeight,
  initialValue: rawInitialValue,
  onChange,
  actions,
  preserveLineBreaks = false,
  format = 'markdown',
}: {
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  initialValue?: string | null;
  actions?: React.ReactNode;
  onChange: (markdown: string) => void;
  preserveLineBreaks?: boolean;
  format?: 'markdown' | 'html';
}) => {
  const initialValue = rawInitialValue ?? '';
  const muiTheme = useTheme();
  const palette = (muiTheme.vars ?? muiTheme).palette;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const preserveLineBreaksRef = useRef(preserveLineBreaks);
  preserveLineBreaksRef.current = preserveLineBreaks;
  const formatRef = useRef(format);
  formatRef.current = format;

  const serialize = useCallback((editor: Quill): string => {
    if (editor.getText().trim().length === 0) {
      return '';
    }
    if (formatRef.current === 'html') {
      return sanitizeHtml(editor.getSemanticHTML()).replace(
        /\u00A0|&nbsp;/g,
        ' ',
      );
    }
    return convertHtmlToMarkdown(editor.getSemanticHTML());
  }, []);

  const seedEditor = useCallback((editor: Quill, value: string) => {
    if (formatRef.current === 'html' && startsAsHtml(value)) {
      editor.setContents(editor.clipboard.convert({ html: value }), 'silent');
    } else {
      const html = markdownToHtml(value, preserveLineBreaksRef.current);
      editor.setContents(editor.clipboard.convert({ html }), 'silent');
    }
  }, []);

  const handleChange = useCallback((serialized: string) => {
    onChangeRef.current(serialized);
  }, []);

  const { editorRef, quill, isBlank, editorKey } = useQuillEditor({
    value: initialValue,
    onChange: handleChange,
    serialize,
    seedEditor,
    formats: MARKDOWN_FORMATS,
    modules: { history: { delay: 1000, maxStack: 100, userOnly: true } },
    readOnly,
    matchers: MARKDOWN_MATCHERS,
    remountDeps: [readOnly],
  });

  return (
    <RichEditorOutline disabled={readOnly}>
      <div
        css={{
          ...qlEditorSurfaceStyles,
          '.ql-editor': {
            ...qlEditorBaseStyles,
            outline: 'none',
            minHeight: minHeight ?? 80,
            padding: '8px 16px',
            opacity: readOnly ? 0.5 : 1,
            maxHeight,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            fontSize: '16px',
          },
          '.ql-editor p': {
            padding: 0,
            margin: `0 0 ${preserveLineBreaks ? 0 : 15}px 0`,
          },
          ...qlListStyles,
          '.editor-placeholder': {
            color: palette.grey[500],
            overflow: 'hidden',
            position: 'absolute',
            top: 0,
            left: 0,
            padding: '8px 16px',
            userSelect: 'none',
            pointerEvents: 'none',
            fontSize: '16px',
          },
        }}
      >
        <ComposerToolbar quill={quill} disabled={readOnly} actions={actions} />
        <div css={{ position: 'relative' }}>
          <div key={editorKey} ref={editorRef} />
          {placeholder != null && isBlank && (
            <div className="editor-placeholder">{placeholder}</div>
          )}
        </div>
      </div>
    </RichEditorOutline>
  );
};
